import { Module } from "vuex";
import { RootState } from "@/types";
import { downloadFile, saveFile } from "@/store/services/offerService";
import {
    forceDocument,
    getOfferDocument,
    getProcess,
    getProcessOrch,
    getRequiredDocument, getRequiredDocumentModifyCondition,
    getRequiredDocumentValidatedByBR,
    normalizeSupportingType,
    qualifyFile,
    receiveFile,
    sendToControl
} from "../../../services/task/actions/uploadDocumentService";
import i18n from "@/i18n";
import { deleteProcessFile } from "@/store/services/task/actions/validateDocumentService";
import { updateStatusReceivedFile } from "@/commons/gathering-process";
import store from "@/store";
import { PDFDocument } from "pdf-lib";
import { concatFile, replaceFile } from "@/store/services/configuration/documentsService";
import Notify from 'quasar/src/plugins/Notify.js';;
import * as pdfjsLib from "pdfjs-dist";
import { getFileBlobWithCache } from "@/store/services/order/uploadedDocumentCache";

const errorTreatment = (options: Options) => {
    const {
        error,
        action = ''
    } = options;
    console.error(`${action} action => ${error.response?.data?.title ?? ''} \n ${error}`)
}

interface Options {
    error: any,
    action: string
}

const uploadDocumentModule: Module<any, RootState> = {
    namespaced: true,
    state: {
        uploadDocumentLoading: {
            "status": false,
            "loadingIndex": null
        },
        // Analyses IA en cours, par document (clé = `${resourceUid}_${childId}_${index}`).
        // Permet plusieurs analyses IA en parallèle sans bloquer les autres uploads.
        aiLoadingIndexes: {} as Record<string, boolean>,
        // Traitement IA depuis le panneau d'upload (boutons intelligent).
        uploadPanelAiLoading: false
    },
    getters: {
        getUploadDocumentLoading(state) {
            return state.uploadDocumentLoading
        },
        getAiLoadingIndexes(state) {
            return state.aiLoadingIndexes
        },
        getUploadPanelAiLoading(state) {
            return state.uploadPanelAiLoading
        }

    },
    actions: {

        uploadTempFiles: ({ commit }, paylod) => {
            commit('setFiles', paylod)
        },
        addTempFiles: ({ commit }, paylod) => {
            commit('addFiles', paylod)
        },
        process:async ({ commit }, payload) => {
             const { processid, callback } = payload
              await getProcess(processid).then(async (res: any) => {
                    if (callback) {
                        callback(res);
                    }
              })
             .catch((e: any) => errorTreatment({ error: e, action: 'concatFile' }))
        },
        uploadFile: async ({ commit }, payload) => {
            const { request, document, processid, documentType, callback , documentReceivedFileID , filePanelIndex, iaVerification} = payload
            try {
            await saveFile(request).then(response => {
                if (callback) {
                    // Chaîne attendue jusqu'au callback : l'appelant sait alors le tiroir
                    // réellement alimenté (receivedFilesUid connu), ce qu'exige un dépôt
                    // suivant dans le même tiroir pour passer par la concaténation (EFLO-1048).
                    return getProcess(processid).then(async (res: any) => {
                        for (const element of res.gatheringProcessItem){
                            const authName= store.getters["authModule/getAuthName"];
                            const file = {
                                fileReference: {
                                    objectType: response.objectType,
                                    systemUid: response.systemUid,
                                    resourceUid: response.resourceUid
                                },
                                creationUser : authName,
                                creationLabel: response.originalFileName,
                                qualification: [
                                    {
                                        documentType: {
                                            resourceUid: documentType
                                        },
                                        label: response.originalFileName,
                                        nbrOfPages: response.nbrOfPages
                                    }
                                ],
                                targetEntityId: document?.targetEntity?.resourceUid,
                                systemUid: 'odm-supportingdocument',
                                // Commentaire issu de la validation IA (issues / champs manquants)
                                receivedComment: iaVerification?.comments || ''
                            }

                            await receiveFile(processid, element.resourceUid, file).then(async res => {
                                response.receivedFilesUid = res.resourceUid
                                if(documentReceivedFileID){
                                    deleteProcessFile(processid,documentReceivedFileID )
                                }
                            await qualifyFile(processid, element.resourceUid, res.resourceUid, file).then(async qualificationRes => {
                                    if (!store.getters["taskModule/isTaskUploadDocuments"]){
                                    if (document && qualificationRes.status.resourceUid === "QUALIFIED") {
                                        document.status = i18n.global.t(
                                            `task.uploadDocument.headers.supportingDocumentStatus.QUALIFIED`
                                        );
                                    }
                                    }
                                    // Persistance du verdict de la validation IA : on écrase le statut
                                    // "qualifié" par celui renvoyé par l'IA (VALIDATED / HUMAN_REVIEW / REJECTED)
                                    if (iaVerification?.status) {
                                        await updateStatusReceivedFile({
                                            status: {
                                                objectType: "odm.supportingdocument.receivedfilestatus",
                                                systemUid: "odm-supportingdocument",
                                                resourceUid: iaVerification.status
                                            }
                                        }, res.resourceUid)
                                    }
                                })
                            })
                            callback(response)

                        }
                    });
                }
            })
            } catch (error: any) {
                console.error("Erreur lors de l'envoi du fichier :", error);
            } finally {
                await store.dispatch("taskModule/uploadDocumentModule/setUploadDocumentLoading", {
                    status: false,
                    loadingIndex: null
                });
                 Notify.create({
                       message: i18n.global.t("order.messages.uploadEnd"),
                       color: "positive",
                })
                filePanelIndex !== undefined && filePanelIndex !== null && filePanelIndex !== ''
                && Number.isInteger(Number(filePanelIndex)) && Number(filePanelIndex) >= 0
                ? store.dispatch("taskModule/uploadDocumentModule/removeFile",  { index: Number(filePanelIndex) })
                : null
            }

        },
        downloadFile: ({ commit }, payload) => {
            const { request, row, callback } = payload
            downloadFile(request.resourceUid).then(response => {
                if (callback) {
                    callback(response, row)
                }
            })
                .catch(e => errorTreatment({ error: e, action: 'downloadFile' }))
        },
        replaceFile: ({ commit }, payload) => {
            const { request, callback } = payload
            concatFile(request).then((response: any) => {
                if (callback) {
                    callback(response, request)
                }
            })
                .catch((e: any) => errorTreatment({ error: e, action: 'downloadFile' }))
        },

        mergePDFs: async ({ commit }, payload) => {
            const mergedPdf = await PDFDocument.create();
            // Ensure we process documents sequentially (avoid async forEach race) and handle
            // encrypted PDFs (empty password) by falling back to pdfjs to decrypt then
            // loading bytes with pdf-lib. This prevents the blank-page issue caused by
            // mergedPdf.save() running before pages were actually added.
            for (const fileDocument of payload.selectedDocuments) {
                const fileResourceUid = fileDocument?.documentUpload?.resourceUid;
                const originalFileName = fileDocument?.documentUpload?.originalFileName || fileDocument?.originalFileName || "document";
                if (!fileResourceUid) {
                    console.warn("Missing file resource uid in selected document", fileDocument);
                    continue;
                }
                const blob = await getFileBlobWithCache(fileResourceUid, originalFileName);
                if (!blob) {
                    console.warn(`Unable to load blob for resourceUid=${fileResourceUid}`);
                    continue;
                }
                const arrayBuffer = await blob.arrayBuffer();
                // Debug info
                console.debug(`Merging resourceUid=${fileResourceUid} - bytes=${arrayBuffer?.byteLength ?? 0}`);

                let pagesAdded = 0;

                // Prefer loading directly with pdf-lib. If it fails (e.g. encrypted PDF),
                // use PDF.js to open with an empty password and obtain decrypted bytes.
                try {
                const pdfLibDoc = await PDFDocument.load(new Uint8Array(arrayBuffer), { ignoreEncryption: true });
                const pages = await mergedPdf.copyPages(pdfLibDoc, pdfLibDoc.getPageIndices());
                pages.forEach((page) => mergedPdf.addPage(page));
                pagesAdded += pages.length;
                console.debug(`pdf-lib loaded ${pages.length} pages for ${fileResourceUid}`);
                } catch (loadErr) {
                console.warn(`pdf-lib failed to load ${fileResourceUid}, trying PDF.js fallback`, loadErr);
                try {
                    const pdfJsDoc = await pdfjsLib.getDocument({ data: arrayBuffer, password: "" }).promise;
                    // Try to obtain decrypted bytes if available
                
                    
                    const decrypted: Uint8Array | undefined = pdfJsDoc.getData ? await pdfJsDoc.getData() : undefined;
                    if (decrypted) {
                    try {
                        const pdfLibDoc = await PDFDocument.load(decrypted, { ignoreEncryption: true });
                        const pages = await mergedPdf.copyPages(pdfLibDoc, pdfLibDoc.getPageIndices());
                        pages.forEach((page) => mergedPdf.addPage(page));
                        pagesAdded += pages.length;
                        console.debug(`pdfjs.getData -> pdf-lib loaded ${pages.length} pages for ${fileResourceUid}`);
                    } catch (err) {
                        console.warn("pdf-lib.load failed on decrypted bytes, will try rendering pages", err);
                    }
                    }

                    try {
                    console.debug(`Rendering pages via PDF.js for resourceUid=${fileResourceUid}`);
                        // Render each page at a higher DPI to preserve original quality.
                        // PDF units are 72 DPI, so choose a render DPI (e.g. 150) and compute scale = dpi/72.
                        const renderDpi = (payload && payload.renderDpi) ? payload.renderDpi : 150;
                        const scale = Math.max(1, renderDpi / 72);
                        for (let p = 1; p <= pdfJsDoc.numPages; p++) {
                        const page = await pdfJsDoc.getPage(p);
                        const viewport = page.getViewport({ scale });

                        // Create canvas at the viewport pixel size
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        if (!ctx) throw new Error('Could not get canvas context');
                        canvas.width = Math.ceil(viewport.width);
                        canvas.height = Math.ceil(viewport.height);

                        // Render the PDF page into the canvas
                        await page.render({ canvasContext: ctx, viewport }).promise;

                        // Convert canvas to Blob (PNG) without using dataURL (preserves quality and is faster)
                        const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
                        if (!blob) throw new Error('Failed to convert canvas to blob');
                        const imgBuf = await blob.arrayBuffer();

                        // Embed PNG into merged PDF
                        const embeddedImg = await mergedPdf.embedPng(imgBuf as any);
                        const imgWidthPx = embeddedImg.width; // pixels
                        const imgHeightPx = embeddedImg.height; // pixels

                        // Convert pixel dimensions back to PDF points so output has correct physical size
                        const pageWidthPts = imgWidthPx / scale;
                        const pageHeightPts = imgHeightPx / scale;

                        const newPage = mergedPdf.addPage([pageWidthPts, pageHeightPts]);
                        newPage.drawImage(embeddedImg, { x: 0, y: 0, width: pageWidthPts, height: pageHeightPts });
                        pagesAdded++;
                        }
                    console.debug(`Rendered and embedded ${pagesAdded} pages for ${fileResourceUid}`);
                    } catch (renderErr) {
                    console.error("Render fallback failed for resourceUid=" + fileResourceUid + ":", renderErr);
                    }
                } catch (err) {
                    console.error("Could not load PDF for merging (resourceUid=" + fileResourceUid + "):", err);
                }
                }
            }

            const mergedPdfBytes = await mergedPdf.save();
            // mergedPdf.save() returns a Uint8Array; cast to any to satisfy BlobPart typing
            const blob = new Blob([mergedPdfBytes as any], { type: "application/pdf" });
            const blobUrl = URL.createObjectURL(blob);

            if (payload.mode === "download") {
                const orderReference = payload.orderReference;
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = orderReference ? `${orderReference}.pdf` : "merged.pdf";
                // Adds the link to the page and triggers the download
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else if (payload.mode === "view") {
                // Open the PDF
                window.open(blobUrl, "_blank");
            }
        },
        getOfferDocument: ({ commit }, payload) => {
            const { request, callback } = payload
            getOfferDocument(request.variable).then(response => {
                if (callback) {
                    callback(response)
                }
            })
        },
        getProcess: ({ commit }, payload) => {
            const { request, callback } = payload
            if (request?.requiredDocuments) {
                normalizeSupportingType(request.requiredDocuments)
            }
            getProcessOrch(request.resourceUid,request.orderId,request.requiredDocuments).then(response => {
                if (callback) {
                    callback(response, request.res)
                }
            })
        },
        getRequiredDocument: ({ commit }, payload) => {
            const { request, callback } = payload
            getRequiredDocument(request.resourceUid).then(response => {
                if (callback) {
                    callback(response)
                }
            })
        },
        getRequiredDocumentValidatedByBR: ({ commit }, payload) => {
            const { request, callback } = payload
            getRequiredDocumentValidatedByBR(request.resourceUid,request.orderId,request.module).then(response => {
                if (callback) {
                    callback(response)
                }
            })
        },
        getRequiredDocumentModifyCondition: ({ commit }, payload) => {
            const { request, callback } = payload
            getRequiredDocumentModifyCondition(request.resourceUid).then(response => {
                if (callback) {
                    callback(response)
                }
            })
        },
        gatheringProcess: ({ commit }, payload) => {
            const { request, callback } = payload
            commit('setGatheringProcess', request.gatheringProcess)
        },
        sendToControl: ({ commit }, payload) => {
            const { resourceUid, callback } = payload
            sendToControl(resourceUid).then(response => {
                if (callback) {
                    callback(response)
                }
            })

        },
        setUploadDocumentLoading({commit}, payload) {
            commit('setUploadDocumentLoading',payload)
        },
        setAiLoading({commit}, payload) {
            commit('setAiLoading', payload)
        },
        removeFile({ commit }, payload) {
            commit("removeFile", payload)
        },
        forceDocument: async ({ commit }, payload) => {
            const { document,receivedComment,status, callback } = payload
            try {
                const response = await forceDocument(document,receivedComment,status)
                if (callback) callback(response)
            } catch (error) {
                console.error(status === 'OVERWRITE' ? "Erreur lors du forçage du document :" : "Erreur lors du commentaire du document :" , error)
            }
        },
    },
    mutations: {
        setFiles(state, payload) {
            state.files = payload
        },
        addFiles(state, payload) {
            if(state.files && state.files.length && state.files.length>0){
                state.files.push(payload) 
            }else{
                state.files=[payload]
            }
            
        },
        setUploadDocumentLoading(state, payload) {
            (state as any).uploadDocumentLoading = payload;
        },
        setAiLoading(state, payload) {
            const { loadingIndex, active, fileName } = payload || {};
            if (!loadingIndex) return;
            // Nouvelle référence d'objet pour garantir la réactivité (add/remove d'index)
            const map = { ...(state as any).aiLoadingIndexes };
            if (active) {
                // La valeur porte le nom du fichier analysé quand il est connu, `true` sinon :
                // l'écran affiche ce nom à la place de la zone de dépôt le temps de l'analyse.
                // Les deux valeurs sont vraies, les lecteurs qui ne testent que la présence
                // ne changent pas de comportement.
                map[loadingIndex] = fileName || true;
            } else {
                delete map[loadingIndex];
            }
            (state as any).aiLoadingIndexes = map;
        },
        setUploadPanelAiLoading(state, payload) {
            state.uploadPanelAiLoading = payload;
        },
        removeFile(state, payload) {
            const index = Number(payload?.index)
            if (!Number.isInteger(index) || index < 0 || index >= (state.files?.length || 0)) {
                return
            }
            state.files?.splice(index, 1);
        }
    }
}

export default uploadDocumentModule